import { Action, ActionReducer, createReducer, MetaReducer, on } from '@ngrx/store'
import {
    initializeMessages,
    newAccommodationData,
    newChatMessage,
    newSaleData,
    newMessage,
    profileUpdateSuccess,
    setCorpMemberProfileData,
    updateMessages,
    updateReadReceipts,
    initializePostsHistory,
    deletePostedPost,
} from '../actions/corp-member.actions'
import {
    AppState,
    CorpMemberState,
    FeedDataType,
    AppMessages,
    initialCorpMemberState,
    initialState,
    ChatMessage,
    AppMessageValue,
    SaleType,
    AccommodationType,
    AppFeedState,
    PostHistoryState,
} from '../app.state'
import { selectFeatureMessages } from '../selectors/corp.selectors'
import { CallerService } from '../../services/caller.service'

export const corpMemberReducer = createReducer(
    initialCorpMemberState, // or initialState.corper // initial state should be the corper from the whole app state
    on(setCorpMemberProfileData, (state: CorpMemberState, { data }) => {
        /**
         * update local storage
         * 
         * on mobile, this doesn't update fast enough so auth fails on /home page
         * fix is to make the update before the dispatching the event in login component
         */
        try {
            localStorage.setItem(CallerService.LOCAL_STORAGE_DATA_KEY, JSON.stringify(data));
        } catch (error) {
            console.log('st err set', error)
            // todo: show notification??
        }

        return data
    }),

    /**
     * Update part of the corper state
     */
    on(profileUpdateSuccess, (state: CorpMemberState, { data }) => {
        const _body = {...state, ...data}
        // update local storage
        try {
            localStorage.setItem(CallerService.LOCAL_STORAGE_DATA_KEY, JSON.stringify(_body));
        } catch (error) {
            console.log('st err update', error);
        }
        return _body
    }),
)

export const feedReducer = createReducer(
    initialState.feed, // initial state should be the feed from the whole app state
    on(newSaleData, (feedState: FeedDataType, { data }) => {
        console.log(feedState, 'just got new sale', data)
        // @todo: should we use browser cache to so some work?

        // map, so we can easily avoid duplicates.
        return {
            ...feedState, 
            sales: new Map([
                ...data?.map((sale: SaleType | any) => [sale?.id, sale]),
                ...feedState.sales,
            ]),
        }
    }),
    on(newAccommodationData, (feedState: FeedDataType, { data }) => {
        console.log(feedState, 'just got new accommodation', data)
        // TODO: should we use browser cache to so some work?
        return {
            ...feedState,
            accommodations: new Map([...data?.map((accommodation: AccommodationType | any) => [accommodation?.id, accommodation]), ...feedState.accommodations]),
        }
    }),
)

/**
 * If there's no new message before; initialize a new one.
 * TODO: update local storage with new states?
 */
export const messagesReducer = createReducer(
    initialState.messages, // initial state should be the messages from the whole app state
    on(initializeMessages, (state, { type, ...data }) => {
        return new Map(Object.entries(data))
    }),
    on(updateMessages, (state: AppMessages, { type, ...data }) => {
        // update logic: old data, then new data
        console.log('?? PRESSED ??', typeof data === 'object')
        
        if (typeof data === 'object') {
            const newData = new Map<string, AppMessageValue>(Object.entries(data))
            console.log('newData', newData)
            return new Map(
                [...state, ...newData]
            )
        }
        return state
    }),

    on(
        newMessage,
        (state: AppMessages, { 
            room, recipient_id, recipient_state_code, 
            initiator_state_code, initiator_name, 
            initiator_id, recipient_name,
            chat_display_name, chat_display_state_code,
        }) =>
        {
            /**
             * @todo: this is not the best way to do this, but it works for now.
             * We should probably use a more efficient way to check if the chat already exists.
             * we should use the recipient_id and the initiator_id as the key of the map, and to check if the chat already exists.
             */
            // find if there's existing chat, find it.
            for (const [key, value] of state) {
                const hasExistingChat =
                    (value.recipient_id === recipient_id && value.initiator_id === initiator_id) ||
                    (value.recipient_id === initiator_id && value.initiator_id === recipient_id)
                if (hasExistingChat) {
                    console.log('found existing chat', key)
                    // we can't sort or order items in map, stays as they were inserted
                    const newState = new Map([...state])
                    newState.delete(key)
                    // so it goes to the beginning.
                    newState.set(key, value)

                    // TODO: best way to make a copy???
                    return newState
                }
            }

            const new_chat_room = window?.crypto?.randomUUID?.() ?? Math.random().toString(36).substring(2)
            return new Map([
                ...state,
                [
                    new_chat_room,
                    {
                        texts: [],
                        unread_messages: 0,
                        recipient_state_code,
                        initiator_state_code,
                        recipient_name,
                        initiator_name,
                        recipient_id,
                        initiator_id,
                        chat_display_name, chat_display_state_code,
                    },
                ],
            ])
        }
    ),
    on(
        newChatMessage,
        (state: AppMessages, chatMessage: ChatMessage) => {
            const _corp_member_id: number = JSON.parse(new String(localStorage.getItem(CallerService.LOCAL_STORAGE_DATA_KEY)).toString())?.id
            return new Map([
                ...state,
                [
                    chatMessage.room,
                    {
                        ...state.get(chatMessage.room),
                        /**
                         * increment unread message (if they have the chat open, update the count to 0)
                         * 
                         * if the message is not from the current user, increment unread_messages
                         */
                        unread_messages: 
                            chatMessage.message_from !== _corp_member_id
                                ? Number(state.get(chatMessage.room)?.unread_messages || 0) + 1
                                : (state.get(chatMessage.room)?.unread_messages || 0),
                        texts: [...state.get(chatMessage.room)?.texts ?? [], chatMessage]
                    },
                ],
            ])
        },
    ),

    on(
        updateReadReceipts,
        (state: AppMessages, data) => {
            const _corp_member_id: number = JSON.parse(new String(localStorage.getItem(CallerService.LOCAL_STORAGE_DATA_KEY)).toString())?.id
            return new Map([
                ...state,
                [
                    data.room,
                    {
                        ...state.get(data.room),
                        // main update
                        unread_messages: 0,
                        /**
                         * only update read_by_to if it's same with recipient_id === _corp_member_id
                         * 
                         * this is kinda too much of a computation??? maybe
                         */
                        texts: [...(state.get(data.room)?.texts ?? [])?.map(
                            (t: ChatMessage) => {
                                if (t.message_to === _corp_member_id) {
                                    return {...t, read_by_to: true}
                                }
                                return t
                            }
                        )]
                    },
                ],
            ])
        },
    ),
)

export const postHistoryReducer = createReducer(
    initialState.posted_items,
    on(initializePostsHistory, (state: PostHistoryState, { data }) => {
        // console.log('das state', state)
        // console.log('continue', data)
        return new Map([...state, ...data])
    }),
    on(deletePostedPost, (state: PostHistoryState, { id }) => {
        const newState = new Map([...state])
        newState.delete(id)
        
        // TODO: best way to make a copy???
        return newState
    }),
)

// Logger Meta-Reducer (from claude.ai)
export function logger(reducer: ActionReducer<any>): ActionReducer<any> {
    return (state, action) => {
        // uncomment to debug ngrx store
        // console.log('-state', state);
        // console.log('-action', action);

        return reducer(state, action);
    };
}

// Add this to your meta-reducers array (from claude.ai)
export const metaReducers: MetaReducer<any>[] = [logger];